/**
 * Single source of truth: assignee display-name -> canonical Auth UID.
 *
 * Imported by the CRA client (TaskService.createTask, taskModel.js). The Node
 * migration scripts DO NOT import this file directly (see below) — they build the
 * same map from the same data via the shared pure logic in `assigneeMap.js`.
 *
 * DERIVED FROM CANON (Slice 1a): the DATA is `src/canon/people.json` (one source);
 * the LOGIC is `src/services/assigneeMap.js` (one source, no imports). This file is
 * the browser entry: it does the webpack-side JSON import and wraps the pure logic
 * so the public API (`NAME_TO_UID`, `deriveAssigneeUids`) is unchanged. Do not
 * hand-edit UIDs here — edit people.json and re-publish.
 *
 * PURE ESM (named `export`s only): a file that mixes `module.exports` with ESM
 * syntax makes webpack treat it as a harmony module and throws at bootstrap,
 * blanking the entire React render (HOTFIX 2026-06-25). Keep it ESM-only.
 *
 * The JSON import here is BARE (no `with { type: 'json' }` attribute): CRA's build
 * (babel-preset-react-app + webpack) only accepts the bare form; the attribute form
 * parses in babel but is re-emitted in a legacy syntax webpack rejects. Node ESM is
 * the opposite (it REQUIRES the attribute) — which is exactly why the Node scripts
 * import people.json themselves with the attribute + assigneeMap.js, instead of
 * importing this browser-only module. One data source, one logic source, two thin
 * env-specific entries. This file must stay inside `src/` so CRA's ModuleScopePlugin
 * allows the client to import it and the canon JSON.
 *
 * Auth UIDs are verified in people.json against live Firebase Auth + the `users`
 * collection — these are Auth UIDs, NOT Notion user IDs or `teachers` doc IDs.
 */

import people from '../canon/people.json';
import { buildNameToUid, deriveAssigneeUidsFrom } from './assigneeMap';

/** name (lowercased alias) -> canonical Auth UID, built from people.json. */
export const NAME_TO_UID = buildNameToUid(people);

/**
 * Map an array of assignee display names to canonical Auth UIDs.
 * Matches case-insensitively on the trimmed name. Unresolved names are dropped
 * (so an all-unresolved list yields []). Returns a de-duped array.
 * @param {string[]} assignedTo - assignee display-name strings
 * @returns {string[]} canonical Auth UIDs
 */
export function deriveAssigneeUids(assignedTo) {
	return deriveAssigneeUidsFrom(assignedTo, NAME_TO_UID);
}
